import { z } from "zod";
/**
 * 駆け込み寺（定期開催の相談会）の予約管理の入出力契約。
 *
 * 2 つの経路がある:
 * - 事務局（ADMIN）が管理画面で開催回（イベント）と時間枠を作る / 予約状況を見る
 * - LP（kansaiaihub.com）の予約ページが、Vercel Function 経由の server-to-server で
 *   受付中の開催回を取得し、予約を送る（共有鍵認証。お問い合わせの取り込みと同じ方式）
 *
 * 時刻の扱い:
 * - 開催日は JST の暦日 `"YYYY-MM-DD"`、時間枠は JST の壁時計 `"HH:MM"` で持つ。
 *   開催日と時刻を UTC の瞬間に変換して保存すると、表示のたびに TZ 変換が要り、
 *   日付をまたぐ枠（例: 23:30〜）で開催日がずれる。
 * - 受付締切だけは「この瞬間を過ぎたら受け付けない」という判定に使うので、
 *   オフセット付きの ISO 8601（瞬間）で受ける。
 */
const TITLE_MAX = 200;
const VENUE_MAX = 300;
const DESCRIPTION_MAX = 5_000;
const NAME_MAX = 200;
const EMAIL_MAX = 320;
const NOTE_MAX = 2_000;
/** 1 開催あたりの時間枠の上限。1 日を 15 分刻みにしても収まる数。 */
export const KAKEKOMI_MAX_SLOTS = 60;
/** 1 枠あたりの参加上限の上限。相談会の規模として十分大きく、誤入力（桁違い）を弾ける値。 */
export const KAKEKOMI_MAX_CAPACITY = 1_000;
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** JST の暦日 `"YYYY-MM-DD"`。2 月 30 日のような実在しない日付も弾く。 */
export const kakekomiDateSchema = z
    .string()
    .regex(DATE_RE, "日付は YYYY-MM-DD 形式で入力してください")
    .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}, "存在しない日付です");
/** JST の壁時計 `"HH:MM"`（24 時間表記）。 */
export const kakekomiTimeSchema = z.string().regex(TIME_RE, "時刻は HH:MM 形式で入力してください");
/** `"HH:MM"` を 0 時からの分に直す。前後比較・重なり判定に使う。 */
export function kakekomiTimeToMinutes(time) {
    const [h, m] = time.split(":").map((s) => parseInt(s, 10));
    return (h ?? 0) * 60 + (m ?? 0);
}
/**
 * 枠の終了の瞬間（開催日 + 終了時刻を JST として解釈）。
 *
 * **枠は終了時刻まで予約できる**（運用判断。進行中の枠にも途中から入れる）。LP への表示・予約の受付・
 * 締切の検証（締切は最後の枠の終了より前）がすべてこの 1 つの計算を使う。
 */
export function kakekomiSlotEndsAt(eventDate, endTime) {
    return new Date(`${eventDate}T${endTime}:00+09:00`);
}
/** 枠がまだ終わっていないか（= 予約を受け付けてよい時間か）。 */
export function isKakekomiSlotOpen(eventDate, endTime, now) {
    return kakekomiSlotEndsAt(eventDate, endTime).getTime() > now.getTime();
}
// ────────────────────────────────────────────────────────────────────────────
// 管理画面（ADMIN）
// ────────────────────────────────────────────────────────────────────────────
export const kakekomiSlotInputSchema = z.object({
    /**
     * 既存の枠を更新するときだけ付ける。新しく足す枠は付けない。
     * 付いていない既存枠は「削除された」とみなす（イベント更新は枠の一括置換）。
     */
    id: z.string().min(1).max(64).optional(),
    startTime: kakekomiTimeSchema,
    endTime: kakekomiTimeSchema,
    capacity: z
        .number({ error: "参加上限を入力してください" })
        .int("参加上限は整数で入力してください")
        .min(1, "参加上限は 1 以上にしてください")
        .max(KAKEKOMI_MAX_CAPACITY, `参加上限は ${KAKEKOMI_MAX_CAPACITY} 以下にしてください`),
});
/**
 * 時間枠一覧の整合性。フロント（フォームのエラー表示）とサーバーで同じ判定を使うため、
 * スキーマの外に関数として持つ（`findReferrerIssues` と同じ方針）。
 *
 * - 終了は開始より後
 * - 枠どうしが重ならない（端点の一致 = 10:00〜11:00 と 11:00〜12:00 は可）
 *
 * 重なりを禁止するのは、LP で「同じ時間帯が 2 つ並ぶ」表示になり、利用者がどちらを
 * 選べばよいか分からなくなるため。上限を増やしたいなら 1 枠の上限を上げればよい。
 */
export function findKakekomiSlotIssues(slots) {
    const issues = [];
    slots.forEach((s, index) => {
        if (!TIME_RE.test(s.startTime) || !TIME_RE.test(s.endTime))
            return;
        if (kakekomiTimeToMinutes(s.endTime) <= kakekomiTimeToMinutes(s.startTime)) {
            issues.push({ index, field: "endTime", message: "終了は開始より後にしてください" });
        }
    });
    if (issues.length > 0)
        return issues;
    const sorted = slots
        .map((s, index) => ({ ...s, index }))
        .filter((s) => TIME_RE.test(s.startTime) && TIME_RE.test(s.endTime))
        .sort((a, b) => kakekomiTimeToMinutes(a.startTime) - kakekomiTimeToMinutes(b.startTime));
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        if (kakekomiTimeToMinutes(cur.startTime) < kakekomiTimeToMinutes(prev.endTime)) {
            issues.push({
                index: cur.index,
                field: "startTime",
                message: `${prev.startTime}〜${prev.endTime} の枠と時間が重なっています`,
            });
        }
    }
    return issues;
}
/**
 * 受付締切と開催日時の前後関係。締切は**最後の枠の終了時刻（JST）より前**でなければならない。
 *
 * 締切の打ち間違い（年の取り違え等）で、終わった回が LP に出続け、予約と確認メールが
 * 届き続けるのを防ぐ。最後の枠の終了まで許すのは、当日の飛び込み参加を受けられるようにするため。
 *
 * `deadlineIso` は瞬間（オフセット付き ISO 8601）。形式不正・枠の時刻不正のときは
 * それぞれの欄のエラーに任せ、ここでは判定しない（null を返す）。
 */
export function findKakekomiDeadlineIssue(eventDate, slots, deadlineIso) {
    const deadline = new Date(deadlineIso).getTime();
    if (!DATE_RE.test(eventDate) || Number.isNaN(deadline))
        return null;
    const ends = slots.map((s) => s.endTime).filter((t) => TIME_RE.test(t));
    if (ends.length === 0)
        return null;
    const lastEnd = ends.reduce((a, b) => (kakekomiTimeToMinutes(a) >= kakekomiTimeToMinutes(b) ? a : b));
    const lastEndAt = kakekomiSlotEndsAt(eventDate, lastEnd).getTime();
    if (deadline >= lastEndAt) {
        return `受付締切は最後の枠の終了（${eventDate.replaceAll("-", "/")} ${lastEnd}）より前にしてください`;
    }
    return null;
}
/**
 * 開催回の作成・更新の項目（検証の追加前）。
 *
 * **refine 前の形を別に公開する。** zod v4 は refine を持つ object に `.omit()` / `.extend()` を
 * 使えない（実行時に throw する）ので、管理画面のフォーム（締切を日付・時刻の 2 欄に分ける）は
 * これを `.omit().extend()` して `addKakekomiEventIssues` を付け直す。検証のルールはこの 2 つにしか無い。
 */
export const kakekomiEventBaseSchema = z.object({
    title: z
        .string()
        .trim()
        .min(1, "イベント名を入力してください")
        .max(TITLE_MAX, `イベント名は ${TITLE_MAX} 文字以内で入力してください`),
    eventDate: kakekomiDateSchema,
    venue: z
        .string()
        .trim()
        .min(1, "開催場所を入力してください")
        .max(VENUE_MAX, `開催場所は ${VENUE_MAX} 文字以内で入力してください`),
    description: z
        .string()
        .trim()
        .max(DESCRIPTION_MAX, `案内文は ${DESCRIPTION_MAX} 文字以内で入力してください`),
    /** 受付締切（瞬間）。この時刻を過ぎると LP に出なくなり、予約も 409 で弾く。 */
    bookingDeadline: z.iso.datetime({ offset: true, error: "受付締切の日時が不正です" }),
    /** false の間は LP に出さない（下書き）。 */
    published: z.boolean(),
    slots: z
        .array(kakekomiSlotInputSchema)
        .min(1, "時間枠を 1 つ以上追加してください")
        .max(KAKEKOMI_MAX_SLOTS, `時間枠は ${KAKEKOMI_MAX_SLOTS} 個までです`),
});
/**
 * 開催回の項目間の検証（枠の前後・重なり・枠 id の重複・締切と開催日時の前後）。
 * API の入力とフォームの両方に同じものを付ける。締切の issue は `deadlinePath` に積む
 * （API は `bookingDeadline`、フォームは日付欄）。
 */
export function addKakekomiEventIssues(v, deadlineIso, deadlinePath, ctx) {
    for (const issue of findKakekomiSlotIssues(v.slots)) {
        ctx.addIssue({
            code: "custom",
            path: ["slots", issue.index, issue.field],
            message: issue.message,
        });
    }
    const ids = v.slots.map((s) => s.id).filter((id) => id !== undefined);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: "custom", path: ["slots"], message: "同じ枠が重複しています" });
    }
    const deadlineIssue = findKakekomiDeadlineIssue(v.eventDate, v.slots, deadlineIso);
    if (deadlineIssue) {
        ctx.addIssue({ code: "custom", path: [deadlinePath], message: deadlineIssue });
    }
}
/**
 * 開催回の作成・更新（同じ形）。
 *
 * 更新も**枠ごと一括で送る**。画面は作成と同じフォームで枠の行を足し引きして保存するので、
 * 枠単位の API に分けるとフロント側で差分計算が要り、そこがずれると枠が消えたり増えたりする。
 * 予約の入った枠を消す・上限を予約数より下げる操作はサーバーが 409 で止める。
 */
export const kakekomiEventInputSchema = kakekomiEventBaseSchema.superRefine((v, ctx) => addKakekomiEventIssues(v, v.bookingDeadline, "bookingDeadline", ctx));
/** 予約の状態。取消は事務局だけが行う（予約者自身のキャンセル導線は持たない）。 */
export const kakekomiBookingStatusEnum = z.enum(["CONFIRMED", "CANCELLED"]);
// ────────────────────────────────────────────────────────────────────────────
// LP からの取り込み（server-to-server）
// ────────────────────────────────────────────────────────────────────────────
/**
 * LP の予約フォームの送信内容。
 *
 * 同じメールアドレスで同じ開催回の**別の枠**は予約してよい（運用判断）。
 * **同じ枠に同じメールアドレスの確定中の予約は 1 件まで**で、2 件目は 409 `ALREADY_BOOKED`
 * （DB の部分ユニーク index でも守る）。LP の再送（タイムアウト後のリトライ）が
 * Idempotency-Key の記録前に届いても、席が 2 つ取られない。
 */
export const kakekomiBookingIngestSchema = z.object({
    slotId: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(NAME_MAX),
    email: z.string().trim().email().max(EMAIL_MAX),
    organization: z.string().trim().min(1).max(NAME_MAX),
    note: z.string().trim().max(NOTE_MAX).optional(),
});
// ────────────────────────────────────────────────────────────────────────────
// 表示
// ────────────────────────────────────────────────────────────────────────────
const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];
/** JST の暦日 `"YYYY-MM-DD"` の曜日（"火" など）。暦日なので TZ 変換は挟まない。API のメールと画面で共有する。 */
export function kakekomiWeekday(date) {
    const [y, m, d] = date.split("-").map((s) => parseInt(s, 10));
    return WEEKDAYS[new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()] ?? "";
}
